import {calculateObjectCoords, getBodyScrollLeft, getBodyScrollTop, imgPath} from "../atBrowserUtils"
import NullTableRegion from "../table/NullTableRegion";
import messageResource from "../messageResource"
import LockingSelectionUpdater from "./LockingSelectionUpdater";
import UnlockingSelectionUpdater from "./UnlockingSelectionUpdater";
import DateSelectionType from './DateSelectionType'

export default class LockEventsHandler {
    constructor(dateFormat, dateParser, timestamp, selectionVeil,
                menuCancelHandler, menuCancelScope, menuDateFormat){
        this.menuCancelHandler = menuCancelHandler;
        this.menuCancelScope = menuCancelScope;
        this.menuDateFormat = menuDateFormat;
        this.dateFormat = dateFormat;
        this.dateParser = dateParser;
        this.timestamp = timestamp;
        this.selectionVeil = selectionVeil;
    }

    selectionRemoved()
    {
        if (this.lockMenu)
        {
            this.lockMenu.hide();
        }
    }

    selectionFinished(selection, mouseEvent)
    {
        this.lockMenu = this.getLockMenu(selection);
        this.lockMenu.setCancelHandler(this.menuCancelHandler, this.menuCancelScope);
        if (selection.canBeLocked())
        {
            this.lockMenu.setLockEnabled(true);
            this.lockMenu.setLockHandler(this.getLockHandler(selection));

        }
        else
        {
            this.lockMenu.setLockEnabled(false);
        }

        if (selection.canBeUnlocked())
        {
            this.lockMenu.setUnlockEnabled(true);
            this.lockMenu.setUnlockHandler(this.getUnlockHandler(selection));

        }
        else
        {
            this.lockMenu.setUnlockEnabled(false);
        }

        let panelPosition = this.getLockPanelPosition(
            selection,
            mouseEvent.clientX + getBodyScrollLeft(),
            mouseEvent.clientY + getBodyScrollTop());
        this.lockMenu.show(panelPosition.x, panelPosition.y);
    }

    getLockPanelPosition(selection, clickX, clickY)
    {
        if (selection === NullTableRegion)
            return {x: clickX + 10, y: clickY + 10};

        let topLeftCorner = calculateObjectCoords(selection.getTopLeftVisibleCell().getCellEl());
        let bottomRightTd = selection.getBottomRightVisibleCell().getCellEl();
        let bottomRightCorner = calculateObjectCoords(bottomRightTd);
        bottomRightCorner.x += bottomRightTd.clientWidth;
        bottomRightCorner.y += bottomRightTd.clientHeight;

        if (clickX < topLeftCorner.x)
            clickX = topLeftCorner.x + 10;
        else if (clickX > bottomRightCorner.x)
            clickX = bottomRightCorner.x - 10;
        else
            clickX += 10;

        if (clickY < topLeftCorner.y)
            clickY = topLeftCorner.y + 10;
        else if (clickY > bottomRightCorner.y)
            clickY = bottomRightCorner.y - 10;
        else
            clickY += 10;

        return {x: clickX, y: clickY};
    }

    /**
     * Gets lock handler for specified selection
     */
    getLockHandler(selection)
    {
        let selectedData = selection.getSelectedData();
        let dateFormat = this.dateFormat;
        let dateParser = this.dateParser;
        let timestamp = this.timestamp;
        let selectionVeil = this.selectionVeil;

        return selection.type.accept({
            visitCellSelection()
            {
                return function()
                {
                    let lockManager = new LockUsersManager(selectedData.userIds,
                        dateFormat.formatDate(selectedData.dateRange.dateFrom),
                        dateFormat.formatDate(selectedData.dateRange.dateTo),
                        new LockingSelectionUpdater(selection.lockTable, selection, dateParser, selectionVeil),
                        timestamp);
                    RpcControlManager.saveRpcControlBoundData(lockManager);
                };
            },

            visitDateSelection()
            {
                return function() { lockSelectedDates(selectedData.dateRange.dateFrom, selectedData.dateRange.dateTo); };
            },

            visitRunnerSelection()
            {
                return function() { lockByRunner(selectedData.dateRange.dateFrom, selectedData.dateRange.dateTo); };
            }
        });
    }

    getLockMenu(selection)
    {
        let leh = this;
        let getCommonMenu = function()
        {
            return new LockMenu({
                text: messageResource.getMessage("lock_tt.table.menu.lock_selected"),
                icon: imgPath("/lock_tt/lock-icon.png")
            }, {
                text: messageResource.getMessage("lock_tt.table.menu.unlock_selected"),
                icon: imgPath("/lock_tt/unlock-icon.png")
            });
        };

        let getSliderMenu = function()
        {
            let dateRange = selection.getSelectedData().dateRange;

            let dateToStr = leh.menuDateFormat.formatDate(dateRange.dateTo);

            let lockText, unlockText;
            if (selection.canBeLocked())
            {
                lockText = messageResource.getMessage("lock_tt.table.slider_menu.lock_enabled",
                    [dateToStr]);
                unlockText = messageResource.getMessage("lock_tt.table.slider_menu.unlock_disabled");
            }
            else
            {
                let dateBeforeStr = leh.menuDateFormat.formatDate(dateRange.dateFrom.add(Date.DAY, -1));
                let dateFromStr = leh.menuDateFormat.formatDate(dateRange.dateFrom);
                lockText = messageResource.getMessage("lock_tt.table.slider_menu.lock_disabled");
                unlockText = messageResource.getMessage("lock_tt.table.slider_menu.unlock_enabled",
                    [dateBeforeStr, dateFromStr, dateToStr]);
            }
            return new LockMenu({
                text: lockText,
                icon: imgPath("/lock_tt/lock-icon.png")
            }, {
                text: unlockText,
                icon: imgPath("/lock_tt/unlock-icon.png")
            });
        };

        // TODO hot fix for section.type=null see AT-2490 - this function does not important(AT-3095)
        if(selection.type === null) {
            selection.type = new DateSelectionType()
        }

        return selection.type.accept({
            visitCellSelection: getCommonMenu,
            visitDateSelection: getCommonMenu,
            visitRunnerSelection: getSliderMenu
        })
    }

    /**
     * Gets unlock handler for specified selection
     */
    getUnlockHandler(selection)
    {
        let selectedData = selection.getSelectedData();
        let dateFormat = this.dateFormat;
        let selectionVeil = this.selectionVeil;

        return selection.type.accept({
            visitCellSelection()
            {
                return function()
                {
                    let unlockManager = new UnlockUsersManager(selectedData.userIds,
                        dateFormat.formatDate(selectedData.dateRange.dateFrom),
                        dateFormat.formatDate(selectedData.dateRange.dateTo),
                        new UnlockingSelectionUpdater(selection.lockTable, selection, selectionVeil));
                    RpcControlManager.saveRpcControlBoundData(unlockManager);
                };
            },

            visitDateSelection()
            {
                return function() { unlockSelectedDates(selectedData.dateRange.dateFrom, selectedData.dateRange.dateTo); };
            },

            visitRunnerSelection()
            {
                return function() { unlockByRunner(selectedData.dateRange.dateFrom, selectedData.dateRange.dateTo); };
            }
        });
    }

    userDayClicked(userId, date, dayCell)
    {
        let manager = null;
        let dateStr = this.dateFormat.formatDate(date);
        let lockTable = dayCell.getRow().getTable();
        let selection = lockTable.getSelectionController().getSelection();

        if (dayCell.getValue().locked)
        {
            manager = new UnlockUsersManager([userId], dateStr, dateStr,
                new UnlockingSelectionUpdater(lockTable, selection, this.selectionVeil));
        }
        else
        {
            manager = new LockUsersManager([userId], dateStr, dateStr,
                new LockingSelectionUpdater(lockTable, selection, this.dateParser, this.selectionVeil),
                this.timestamp);
        }

        RpcControlManager.saveRpcControlBoundData(manager, false);
    }
}


// WEBPACK FOOTER //
// ./web/js/locktable/LockEventsHandler.js